import { useEffect, useState } from "react";
import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useValidEmail } from "../../../hooks/useValidEmail";
import { AxiosError } from "axios";
import { useValidName } from "../../../hooks/useValidName";
import { useValidLastName } from "../../../hooks/useValidLastName";
import { useDeleteAccount, useUpdateUser, useUserData } from "../../../api/UserApi";
import { CurrentUser } from "../../../types/CurrentUser";
import { useValidPhoneMask } from "../../../hooks/useValidPhoneMask";
import { THEME_COLORS } from "../../../base/theme/foundations/colors";
import { useHandleModal } from "../../../hooks/useHandleModal";

const GeneralTab = () => {
  const { firstName, setFirstName, nameIsValid } = useValidName(null);
  const { lastName, setLastName, lastNameIsValid } = useValidLastName(null);
  const [address, setAddress] = useState<string>("");
  const { email, setEmail, emailIsValid } = useValidEmail(null);
  const [providerId, setProviderId] = useState<string>("");
  const { data: currentUser } = useUserData();
  const { mutate: deleteAccount } = useDeleteAccount();
  const { ref, phone, invalidPhoneError, parsedPhone } = useValidPhoneMask(
    currentUser?.phone,
  );
  const handleModal = useHandleModal();
  const toast = useToast();

  useEffect(() => {
    setFirstName(currentUser?.firstName ?? "");
    setLastName(currentUser?.lastName ?? "");
    setEmail(currentUser?.email ?? "");
    setProviderId(currentUser?.providerId ?? "");
    setAddress(currentUser?.address ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

  const showDeletionModal = () => {
    handleModal({
      title: "Delete your account?",
      subtitle:
        "This action cannot be undone. Are you sure you want to delete your account?",
      variant: "destructive",
      leftButtonTitle: "Cancel",
      rightButtonTitle: "Delete Account",
      onClickRightButton: () => {
        deleteAccount();
      },
    });
  };

  const onButtonPress = () => {
    if (nameIsValid !== "" || lastNameIsValid !== "") return;
    const params: Omit<CurrentUser, "id" | "email"> = {};
    if (firstName !== currentUser?.firstName) params.firstName = firstName ?? "";
    if (lastName !== currentUser?.lastName) params.lastName = lastName ?? "";
    if (parsedPhone !== currentUser?.phone) params.phone = parsedPhone ?? "";
    if (providerId !== currentUser?.providerId) params.providerId = providerId;
    if (address !== currentUser?.address) {
      params.address = address;
    }

    if (Object.keys(params).length) {
      updateUser(params)
        .then(() => {
          toast({
            title: "Profile updated successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        })
        .catch((err: AxiosError) => {
          toast({
            title: "Error",
            description: err.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    }
  };

  const isButtonDisabled = () => {
    return isUpdating || nameIsValid !== "" || lastNameIsValid !== "";
  };
  return (
    <>
      <Grid mt={20} templateColumns="repeat(3, 1fr)">
        <GridItem colSpan={1} textStyle="h5">
          General Information
        </GridItem>
        <GridItem colSpan={2}>
          <Grid gap={6} backgroundColor="darkBlue2.900" p="24px" borderRadius="16px">
            <HStack>
              <FormControl variant="floating">
                <Input
                  color="white"
                  placeholder="" // leave empty for floating label
                  aria-label="First Name"
                  name="firstName"
                  type="text"
                  autoComplete="First name"
                  required
                  value={firstName ?? ""}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isUpdating}
                />
                <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue2[900] }}>
                  First Name
                </FormLabel>
              </FormControl>
              <FormControl variant="floating">
                <Input
                  color="white"
                  placeholder="" // leave empty for floating label
                  aria-label="Last name"
                  name="lastName"
                  type="text"
                  autoComplete="Last name"
                  required
                  value={lastName ?? ""}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isUpdating}
                />
                <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue2[900] }}>
                  Last name
                </FormLabel>
              </FormControl>
            </HStack>
            <HStack>
              <FormControl variant="floating">
                <Input
                  color="white"
                  placeholder="" // leave empty for floating label
                  aria-label="Email"
                  name="email"
                  type="email"
                  required
                  value={email ?? ""}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled
                  isInvalid={emailIsValid !== ""}
                />
              </FormControl>
              <FormControl variant="floating">
                <Input
                  color="white"
                  placeholder="" // leave empty for floating label
                  aria-label="Address"
                  name="address"
                  type="address"
                  autoComplete=""
                  required
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={isUpdating}
                />
                <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue2[900] }}>
                  Address
                </FormLabel>
              </FormControl>
            </HStack>
            <FormControl variant="floating">
              <Input
                color="white"
                placeholder="" // leave empty for floating label
                aria-label="NPI Number"
                name="providerId"
                type="number"
                required
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                disabled={isUpdating}
              />
              <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue2[900] }}>
                NPI Number
              </FormLabel>
            </FormControl>
            <HStack>
              <FormControl flex={1} variant="floating">
                <Input
                  color="white"
                  placeholder="" // leave empty for floating label
                  aria-label="Phone"
                  name="phone"
                  type="tel"
                  required
                  inputMode="tel"
                  value={phone}
                  ref={ref as React.RefObject<HTMLInputElement>}
                  disabled={isUpdating}
                  isInvalid={invalidPhoneError !== ""}
                  onChange={(e) => {
                    e.preventDefault();
                  }}
                />
                <FormLabel style={{ backgroundColor: THEME_COLORS.darkBlue2[900] }}>
                  Phone
                </FormLabel>
              </FormControl>
            </HStack>
            <Button
              onClick={onButtonPress}
              colorScheme="brandYellow"
              textColor="brandBlue.800"
              isDisabled={isButtonDisabled()}
              isLoading={isUpdating}
            >
              {isUpdating ? "...loading" : "Save changes"}
            </Button>
          </Grid>
        </GridItem>
      </Grid>
      <Divider my="32px" color="gray.600" />
      <Grid templateColumns="repeat(3, 1fr)" justifyContent="space-between">
        <GridItem textStyle="h5">Delete account</GridItem>
        <GridItem backgroundColor="darkBlue2.900" colSpan={2} p="24px" borderRadius="16px">
          <Text color="white" fontWeight="700">
            Deleting your account will permanently remove your data and cannot be undone.
          </Text>
          <Button
            mt="16px"
            variant="outline"
            colorScheme="error"
            className="responsive-button"
            onClick={() => showDeletionModal()}
          >
            Delete account
          </Button>
        </GridItem>
      </Grid>
      <HStack mt={6} justify={"space-between"}>
        <Text color="white" alignSelf={"flex-end"}>
          Version 1.5.0
        </Text>
      </HStack>
    </>
  );
};

export default GeneralTab;
